// did:webvh v1.0 as this rail runs it, ported from did_toolkit/did_webvh.py so
// the browser and the server agree byte for byte: Ed25519-only update keys, no
// witnesses, no pre-rotation, portable only from the first entry, one
// eddsa-jcs-2022 proof per entry signed by an update key of the previous version.
import { sha256 } from "@noble/hashes/sha256";
import { b58, concat } from "./encoding";
import { canonical } from "./jcs";
import { fromMultikey } from "./keys";
import { signEddsaJcs2022, verifyEddsaJcs2022, EDDSA_JCS_2022 } from "./dataIntegrity";

export const METHOD = "did:webvh:1.0";
export const PLACEHOLDER = "{SCID}";
const PARAMETERS = new Set(["method", "scid", "updateKeys", "nextKeyHashes", "witness", "watchers",
  "deactivated", "ttl", "portable"]);
const ENTRY_FIELDS = ["parameters", "proof", "state", "versionId", "versionTime"];
const VM_FIELDS = new Set(["id", "type", "controller", "publicKeyJwk", "publicKeyMultibase"]);
const SCID_RE = /^[1-9A-HJ-NP-Za-km-z]{46}$/;
const FUTURE_TOLERANCE_MS = 5 * 60 * 1000;

export type LogEntry = {
  versionId: string; versionTime: string; parameters: Record<string, any>;
  state: Record<string, any>; proof: Record<string, any>[];
};

export interface Version {
  number: number; versionId: string; versionTime: string; state: Record<string, any>;
  updateKeys: string[]; signer: string; portable: boolean; entry: LogEntry;
}

export class WebvhError extends Error {
  constructor(message: string, public index?: number) {
    super(index === undefined ? message : `entry ${index}: ${message}`);
  }
}

export const sha256Multihash = (data: Uint8Array): string =>
  b58(concat(new Uint8Array([0x12, 0x20]), sha256(data)));

export function parseTime(v: unknown): number {
  if (typeof v !== "string" || !(v.endsWith("Z") || v.endsWith("+00:00")))
    throw new WebvhError(`versionTime ${JSON.stringify(v)} must be UTC with an explicit Z or +00:00`);
  const t = Date.parse(v);
  if (Number.isNaN(t)) throw new WebvhError(`versionTime ${JSON.stringify(v)} is not ISO 8601`);
  return t;
}

export function parseDid(did: string): { scid: string; host: string; path: string[] } {
  const parts = did.split(":");
  if (parts.length < 4 || parts[0] !== "did" || parts[1] !== "webvh" || !parts[2] || !parts[3])
    throw new WebvhError(`not a did:webvh: ${did}`);
  return { scid: parts[2], host: decodeURIComponent(parts[3]), path: parts.slice(4) };
}

export const updateKeyVm = (mk: string) => `did:key:${mk}#${mk}`;
const clone = <T>(o: T): T => JSON.parse(JSON.stringify(o));
const replaceAll = (o: unknown, from: string, to: string) => JSON.parse(JSON.stringify(o).split(from).join(to));

/** The genesis entry of a new DID. `stateTemplate` uses {SCID} wherever the SCID goes. */
export async function createEntry(stateTemplate: Record<string, any>, opts: {
  updateKeys: string[]; versionTime: string; signerMultikey: string;
  sign: (m: Uint8Array) => Promise<Uint8Array>; portable?: boolean;
}): Promise<LogEntry> {
  const params: Record<string, any> = { method: METHOD, scid: PLACEHOLDER, updateKeys: [...opts.updateKeys] };
  if (opts.portable ?? true) params.portable = true;
  const prelim = { versionId: PLACEHOLDER, versionTime: opts.versionTime, parameters: params, state: stateTemplate };
  const scid = sha256Multihash(canonical(prelim));
  const entry: any = replaceAll(prelim, PLACEHOLDER, scid);
  entry.versionId = "1-" + sha256Multihash(canonical({ ...entry, versionId: scid }));
  entry.proof = [await signEddsaJcs2022(entry, opts.sign,
    { verificationMethod: updateKeyVm(opts.signerMultikey), created: opts.versionTime })];
  return entry;
}

export async function nextEntry(prev: LogEntry, state: Record<string, any>, opts: {
  versionTime: string; signerMultikey: string; sign: (m: Uint8Array) => Promise<Uint8Array>;
  parameters?: Record<string, any>;
}): Promise<LogEntry> {
  const n = parseInt(prev.versionId.split("-", 1)[0], 10) + 1;
  const entry: any = { versionId: prev.versionId, versionTime: opts.versionTime,
    parameters: opts.parameters ?? {}, state };
  entry.versionId = `${n}-` + sha256Multihash(canonical(entry));
  entry.proof = [await signEddsaJcs2022(entry, opts.sign,
    { verificationMethod: updateKeyVm(opts.signerMultikey), created: opts.versionTime })];
  return entry;
}

function checkParameters(params: any, i: number) {
  if (!params || typeof params !== "object" || Array.isArray(params)) throw new WebvhError("parameters must be an object", i);
  const unknown = Object.keys(params).filter((k) => !PARAMETERS.has(k));
  if (unknown.length) throw new WebvhError(`unknown parameters ${JSON.stringify(unknown.sort())}`, i);
  if (params.nextKeyHashes !== undefined && !(Array.isArray(params.nextKeyHashes) && !params.nextKeyHashes.length))
    throw new WebvhError("pre-rotation (nextKeyHashes) is not used on this rail", i);
  if (params.witness !== undefined && !(typeof params.witness === "object" && params.witness && !Object.keys(params.witness).length))
    throw new WebvhError("witnesses are off on this rail", i);
  if ("portable" in params && typeof params.portable !== "boolean") throw new WebvhError("portable must be a boolean", i);
  if (i > 0 && params.portable === true) throw new WebvhError("portable: true may be set only in the first entry", i);
  if ("updateKeys" in params) {
    const uk = params.updateKeys;
    if (!Array.isArray(uk) || !uk.every((k) => typeof k === "string")) throw new WebvhError("updateKeys must be a list of multikeys", i);
    for (const mk of uk) {
      let kind;
      try { kind = fromMultikey(mk).kind; } catch (e) { throw new WebvhError(`not a multikey: ${mk}`, i); }
      if (kind !== "Ed25519") throw new WebvhError(`updateKeys holds a ${kind} key; only Ed25519 keys may update a DID`, i);
    }
  }
}

function checkState(state: any, scid: string, i: number) {
  if (!state || typeof state !== "object") throw new WebvhError("state must be a DID document", i);
  if (parseDid(String(state.id ?? "")).scid !== scid) throw new WebvhError("state.id does not carry the DID's SCID", i);
  for (const vm of state.verificationMethod ?? []) {
    if (!vm || typeof vm !== "object") throw new WebvhError("a verification method must be an object", i);
    const extra = Object.keys(vm).filter((k) => !VM_FIELDS.has(k));
    if (extra.length) throw new WebvhError(`verification method ${vm.id} has custom properties ${JSON.stringify(extra.sort())}; roles are carried in the key id`, i);
  }
}

export function verifyEntry(entry: any, i: number, prev: Version | null, now?: number): Version {
  if (!entry || typeof entry !== "object" || JSON.stringify(Object.keys(entry).sort()) !== JSON.stringify(ENTRY_FIELDS))
    throw new WebvhError("an entry has exactly versionId, versionTime, parameters, state and proof", i);
  if (prev && prev.entry.parameters.deactivated) throw new WebvhError("the DID is deactivated", i);
  const params = entry.parameters;
  checkParameters(params, i);
  let scid: string, authorised: string[];
  if (!prev) {
    if (params.method !== METHOD) throw new WebvhError(`the first entry must set method ${METHOD}`, i);
    if (typeof params.scid !== "string" || !("updateKeys" in params)) throw new WebvhError("the first entry must set scid and updateKeys", i);
    scid = params.scid; authorised = params.updateKeys;
  } else {
    if ("scid" in params) throw new WebvhError("scid may only appear in the first entry", i);
    if ("method" in params && params.method !== METHOD) throw new WebvhError(`method must remain ${METHOD}`, i);
    scid = parseDid(prev.state.id).scid; authorised = [...prev.updateKeys];
  }
  let proofs = entry.proof;
  if (proofs && !Array.isArray(proofs)) proofs = [proofs];
  if (!Array.isArray(proofs) || proofs.length !== 1) throw new WebvhError("an entry carries exactly one proof (witnesses are off)", i);
  const proof = proofs[0];
  if (!proof || proof.type !== "DataIntegrityProof" || proof.cryptosuite !== EDDSA_JCS_2022)
    throw new WebvhError(`the proof must be a DataIntegrityProof with cryptosuite ${EDDSA_JCS_2022}`, i);
  if (proof.proofPurpose !== "assertionMethod") throw new WebvhError("the proof's proofPurpose must be assertionMethod", i);
  const vm = proof.verificationMethod;
  const signer = typeof vm === "string" && vm.startsWith("did:key:") && vm.includes("#") ? vm.split("#")[1] : null;
  if (!signer || vm !== updateKeyVm(signer) || !authorised.includes(signer))
    throw new WebvhError(`signed by ${JSON.stringify(vm)}, which is not an active update key`, i);
  const { proof: _p, ...unsecured } = entry;
  if (!verifyEddsaJcs2022(unsecured, proof, fromMultikey(signer))) throw new WebvhError("the proof does not verify", i);
  const versionId = entry.versionId;
  if (typeof versionId !== "string" || versionId.split("-").length !== 2) throw new WebvhError("versionId must be <number>-<entryHash>", i);
  const [number, entryHash] = versionId.split("-");
  const expected = prev ? prev.number + 1 : 1;
  if (number !== String(expected)) throw new WebvhError(`version number ${number}, expected ${expected}`, i);
  if (!prev) {
    if (!SCID_RE.test(scid)) throw new WebvhError("scid is not a base58btc SHA-256 multihash", i);
    const prelim = replaceAll({ ...unsecured, versionId: PLACEHOLDER }, scid, PLACEHOLDER);
    if (sha256Multihash(canonical(prelim)) !== scid) throw new WebvhError("the SCID does not verify", i);
  }
  const predecessor = prev ? prev.versionId : scid;
  if (sha256Multihash(canonical({ ...unsecured, versionId: predecessor })) !== entryHash)
    throw new WebvhError("the entry hash does not verify: the chain is broken", i);
  const t = parseTime(entry.versionTime);
  if (prev && t <= parseTime(prev.versionTime)) throw new WebvhError("versionTime must be later than the previous entry's", i);
  if (now !== undefined && t > now + FUTURE_TOLERANCE_MS) throw new WebvhError("versionTime is in the future", i);
  checkState(entry.state, scid, i);
  const portable = !prev ? params.portable === true : prev.portable && (params.portable ?? true);
  if (prev && entry.state.id !== prev.state.id) {
    if (!prev.portable || !portable) throw new WebvhError("the DID moved, but it is not portable", i);
    if (!(entry.state.alsoKnownAs ?? []).includes(prev.state.id)) throw new WebvhError("a moved DID must list its prior DID in alsoKnownAs", i);
  }
  return { number: expected, versionId, versionTime: entry.versionTime, state: entry.state,
    updateKeys: [...(params.updateKeys ?? authorised)], signer, portable: Boolean(portable), entry: clone(entry) };
}

export function verifyLog(entries: unknown[], did?: string, now?: number): Version[] {
  if (!Array.isArray(entries) || !entries.length) throw new WebvhError("an empty log");
  const out: Version[] = [];
  let prev: Version | null = null;
  entries.forEach((e, i) => { prev = verifyEntry(e, i, prev, now); out.push(prev); });
  if (did && !out.some((v) => v.state.id === did)) throw new WebvhError("no entry's state.id is the DID");
  return out;
}

/** The version in force at `versionTime` (the latest at or before it), or the latest. */
export function select(versions: Version[], at?: { versionId?: string; versionTime?: string }): Version {
  if (at?.versionId) {
    const v = versions.find((x) => x.versionId === at.versionId);
    if (!v) throw new WebvhError(`no version ${at.versionId}`);
    return v;
  }
  if (at?.versionTime) {
    const t = Date.parse(at.versionTime);
    const inForce = versions.filter((v) => parseTime(v.versionTime) <= t);
    if (!inForce.length) throw new WebvhError(`the DID did not exist at ${at.versionTime}`);
    return inForce[inForce.length - 1];
  }
  return versions[versions.length - 1];
}
